import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, {
  formatPrice as formatProductPrice,
  siteConfig,
} from "../Datas/productData";
import "../css/checkout.css";

const initialFormValues = {
  contact: "",
  country: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  addressLineTwo: "",
  city: "",
  region: "",
  postalCode: "",
  shippingMethod: "standard",
  cardNumber: "",
  expiry: "",
  securityCode: "",
  cardName: "",
  billingSame: true,
  billingCountry: "",
  billingAddress: "",
  billingCity: "",
  billingPostalCode: "",
};

const countryOptions = [
  ["NL", "Netherlands"],
  ["BE", "Belgium"],
  ["DE", "Germany"],
  ["FR", "France"],
  ["ES", "Spain"],
  ["IT", "Italy"],
  ["GB", "United Kingdom"],
  ["US", "United States"],
  ["CA", "Canada"],
  ["CN", "China"],
  ["JP", "Japan"],
  ["AU", "Australia"],
];

const toNumericPrice = (price) => {
  if (typeof price === "number") return Number.isFinite(price) ? price : 0;
  const parsedPrice = Number(String(price || "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsedPrice) ? parsedPrice : 0;
};

const formatCurrency = (value) => {
  const amount = Number.isFinite(Number(value)) ? Number(value) : 0;
  return formatProductPrice(amount);
};

const findProduct = (productId) =>
  productData.find((product) => String(product.id) === String(productId));

const isValidExpiry = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 4) return false;

  const month = Number(digits.slice(0, 2));
  const year = 2000 + Number(digits.slice(2));
  if (month < 1 || month > 12) return false;

  const now = new Date();
  const expiryDate = new Date(year, month, 0, 23, 59, 59);
  return expiryDate >= now;
};

const validateForm = (values) => {
  const nextErrors = {};
  const contact = values.contact.trim();
  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact);
  const phoneIsValid = contact.replace(/\D/g, "").length >= 7;
  const cardDigits = values.cardNumber.replace(/\D/g, "");

  if (!emailIsValid && !phoneIsValid) {
    nextErrors.contact = "Enter a valid email address or phone number.";
  }
  if (!values.country) nextErrors.country = "Select a country or region.";
  if (!values.firstName.trim()) nextErrors.firstName = "Enter your first name.";
  if (!values.lastName.trim()) nextErrors.lastName = "Enter your last name.";
  if (!values.address.trim()) nextErrors.address = "Enter a street address.";
  if (!values.city.trim()) nextErrors.city = "Enter a city.";
  if (!values.postalCode.trim()) {
    nextErrors.postalCode = "Enter a postal code.";
  }
  if (cardDigits.length !== 16) {
    nextErrors.cardNumber = "Enter a valid 16-digit card number.";
  }
  if (!isValidExpiry(values.expiry)) {
    nextErrors.expiry = "Enter a valid future expiry date.";
  }
  if (!/^\d{3,4}$/.test(values.securityCode)) {
    nextErrors.securityCode = "Enter a valid security code.";
  }
  if (!values.cardName.trim()) {
    nextErrors.cardName = "Enter the name shown on your card.";
  }
  if (!values.billingSame) {
    if (!values.billingCountry) {
      nextErrors.billingCountry = "Select a billing country or region.";
    }
    if (!values.billingAddress.trim()) {
      nextErrors.billingAddress = "Enter a billing address.";
    }
    if (!values.billingCity.trim()) {
      nextErrors.billingCity = "Enter a billing city.";
    }
    if (!values.billingPostalCode.trim()) {
      nextErrors.billingPostalCode = "Enter a billing postal code.";
    }
  }

  return nextErrors;
};

const CheckOut = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useContext(CartContext);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const orderTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (orderTimerRef.current) window.clearTimeout(orderTimerRef.current);
    },
    []
  );

  const checkoutLines = useMemo(
    () =>
      cartItems
        .map((item) => {
          const product = findProduct(item.id);
          if (!product) return null;

          const quantity = Math.max(1, Math.floor(Number(item.quantity) || 1));
          const unitPrice = toNumericPrice(product.price);
          return {
            ...item,
            product,
            quantity,
            unitPrice,
            lineTotal: unitPrice * quantity,
          };
        })
        .filter(Boolean),
    [cartItems]
  );

  const subtotal = checkoutLines.reduce(
    (runningTotal, item) => runningTotal + item.lineTotal,
    0
  );
  const freeShippingThreshold = Math.max(
    0,
    Number(siteConfig.freeShippingThreshold) || 0
  );
  const shippingRate = Math.max(
    0,
    Number(siteConfig.shippingFlatRate) || 0
  );
  const shipping =
    freeShippingThreshold === 0 || subtotal >= freeShippingThreshold
      ? 0
      : shippingRate;
  const orderTotal = subtotal + shipping;

  const updateField = (name, value) => {
    setFormValues((currentValues) => ({ ...currentValues, [name]: value }));
    if (errors[name]) {
      setErrors((currentErrors) => {
        const nextErrors = { ...currentErrors };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const handleInputChange = (event) => {
    const { checked, name, type, value } = event.target;
    updateField(name, type === "checkbox" ? checked : value);
  };

  const handleCardNumberChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 16);
    updateField("cardNumber", digits.replace(/(\d{4})(?=\d)/g, "$1 "));
  };

  const handleExpiryChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
    const formattedValue =
      digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
    updateField("expiry", formattedValue);
  };

  const handleSecurityCodeChange = (event) => {
    updateField(
      "securityCode",
      event.target.value.replace(/\D/g, "").slice(0, 4)
    );
  };

  const handleBillingSameChange = (event) => {
    const { checked } = event.target;
    updateField("billingSame", checked);

    if (checked) {
      setErrors((currentErrors) => {
        const nextErrors = { ...currentErrors };
        delete nextErrors.billingCountry;
        delete nextErrors.billingAddress;
        delete nextErrors.billingCity;
        delete nextErrors.billingPostalCode;
        return nextErrors;
      });
    }
  };

  const getErrorAttributes = (fieldName) => ({
    "aria-invalid": Boolean(errors[fieldName]),
    "aria-describedby": errors[fieldName]
      ? `${fieldName}-error`
      : undefined,
  });

  const handlePlaceOrder = (event) => {
    event.preventDefault();
    if (isSubmitting || checkoutLines.length === 0) return;

    const nextErrors = validateForm(formValues);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      const firstInvalidField = Object.keys(nextErrors)[0];
      window.requestAnimationFrame(() => {
        document.getElementById(firstInvalidField)?.focus();
      });
      return;
    }

    setIsSubmitting(true);
    const orderPrefix =
      siteConfig.brandName.replace(/[^a-z0-9]/gi, "").slice(0, 2).toUpperCase() ||
      "ORDER";
    const orderReference = `${orderPrefix}-${Date.now().toString().slice(-8)}`;

    orderTimerRef.current = window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", {
        replace: true,
        state: { orderReference, total: orderTotal },
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 700);
  };

  if (checkoutLines.length === 0) {
    return (
      <main className="checkout-page checkout-page--empty" id="main-content" tabIndex="-1">
        <section className="checkout-empty" aria-labelledby="checkout-empty-title">
          <p className="checkout-eyebrow">Checkout</p>
          <h1 id="checkout-empty-title">Your bag is empty</h1>
          <p>Add an item to your bag before continuing to checkout.</p>
          <Link to="/shop" className="checkout-empty__link">
            Shop the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page" id="main-content" tabIndex="-1">
      <div className="checkout-shell">
        <section className="checkout-form-panel" aria-labelledby="checkout-title">
          <header className="checkout-form-panel__header">
            <p className="checkout-eyebrow">{siteConfig.brandName}</p>
            <h1 id="checkout-title">Checkout</h1>
            <nav aria-label="Checkout progress">
              <Link to="/cartpage">Bag</Link>
              <span aria-hidden="true">/</span>
              <span>Information</span>
              <span aria-hidden="true">/</span>
              <span>Payment</span>
            </nav>
          </header>

          <form className="checkout-form" onSubmit={handlePlaceOrder} noValidate>
            {Object.keys(errors).length > 0 && (
              <div className="checkout-error-summary" role="alert">
                Please review the highlighted fields before placing your order.
              </div>
            )}

            <fieldset className="checkout-section">
              <legend>Contact</legend>
              <div className="checkout-field">
                <label htmlFor="contact">Email or phone number</label>
                <input
                  id="contact"
                  name="contact"
                  type="text"
                  autoComplete="email"
                  value={formValues.contact}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  {...getErrorAttributes("contact")}
                />
                {errors.contact && (
                  <p className="checkout-field__error" id="contact-error">
                    {errors.contact}
                  </p>
                )}
              </div>
            </fieldset>

            <fieldset className="checkout-section">
              <legend>Delivery</legend>

              <div className="checkout-field">
                <label htmlFor="country">Country / region</label>
                <select
                  id="country"
                  name="country"
                  autoComplete="country"
                  value={formValues.country}
                  onChange={handleInputChange}
                  {...getErrorAttributes("country")}
                >
                  <option value="">Select country / region</option>
                  {countryOptions.map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <p className="checkout-field__error" id="country-error">
                    {errors.country}
                  </p>
                )}
              </div>

              <div className="checkout-field-row">
                <div className="checkout-field">
                  <label htmlFor="firstName">First name</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={formValues.firstName}
                    onChange={handleInputChange}
                    {...getErrorAttributes("firstName")}
                  />
                  {errors.firstName && (
                    <p className="checkout-field__error" id="firstName-error">
                      {errors.firstName}
                    </p>
                  )}
                </div>
                <div className="checkout-field">
                  <label htmlFor="lastName">Last name</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={formValues.lastName}
                    onChange={handleInputChange}
                    {...getErrorAttributes("lastName")}
                  />
                  {errors.lastName && (
                    <p className="checkout-field__error" id="lastName-error">
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              <div className="checkout-field">
                <label htmlFor="company">Company (optional)</label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  autoComplete="organization"
                  value={formValues.company}
                  onChange={handleInputChange}
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="address">Address</label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  autoComplete="address-line1"
                  value={formValues.address}
                  onChange={handleInputChange}
                  {...getErrorAttributes("address")}
                />
                {errors.address && (
                  <p className="checkout-field__error" id="address-error">
                    {errors.address}
                  </p>
                )}
              </div>

              <div className="checkout-field">
                <label htmlFor="addressLineTwo">
                  Apartment, suite, etc. (optional)
                </label>
                <input
                  id="addressLineTwo"
                  name="addressLineTwo"
                  type="text"
                  autoComplete="address-line2"
                  value={formValues.addressLineTwo}
                  onChange={handleInputChange}
                />
              </div>

              <div className="checkout-field-row checkout-field-row--three">
                <div className="checkout-field">
                  <label htmlFor="city">City</label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    autoComplete="address-level2"
                    value={formValues.city}
                    onChange={handleInputChange}
                    {...getErrorAttributes("city")}
                  />
                  {errors.city && (
                    <p className="checkout-field__error" id="city-error">
                      {errors.city}
                    </p>
                  )}
                </div>
                <div className="checkout-field">
                  <label htmlFor="region">State / province</label>
                  <input
                    id="region"
                    name="region"
                    type="text"
                    autoComplete="address-level1"
                    value={formValues.region}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="checkout-field">
                  <label htmlFor="postalCode">Postal code</label>
                  <input
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    autoComplete="postal-code"
                    value={formValues.postalCode}
                    onChange={handleInputChange}
                    {...getErrorAttributes("postalCode")}
                  />
                  {errors.postalCode && (
                    <p className="checkout-field__error" id="postalCode-error">
                      {errors.postalCode}
                    </p>
                  )}
                </div>
              </div>
            </fieldset>

            <fieldset className="checkout-section">
              <legend>Shipping method</legend>
              <label className="checkout-shipping-option">
                <span>
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="standard"
                    checked={formValues.shippingMethod === "standard"}
                    onChange={handleInputChange}
                  />
                  <span>
                    Standard tracked shipping
                    <small>Estimated delivery in 3–6 business days</small>
                  </span>
                </span>
                <strong>{shipping === 0 ? "Free" : formatCurrency(shipping)}</strong>
              </label>
            </fieldset>

            <fieldset className="checkout-section checkout-payment">
              <legend>Payment</legend>
              <p className="checkout-section__description">
                All transactions are secure and encrypted.
              </p>

              <div className="checkout-payment__box">
                <div className="checkout-payment__heading">
                  <span>Credit card</span>
                  <span className="checkout-payment__cards" aria-label="Accepted cards">
                    <span>VISA</span>
                    <span>MC</span>
                    <span>AMEX</span>
                  </span>
                </div>

                <div className="checkout-payment__fields">
                  <div className="checkout-field">
                    <label htmlFor="cardNumber">Card number</label>
                    <input
                      id="cardNumber"
                      name="cardNumber"
                      type="text"
                      inputMode="numeric"
                      autoComplete="cc-number"
                      value={formValues.cardNumber}
                      onChange={handleCardNumberChange}
                      placeholder="0000 0000 0000 0000"
                      {...getErrorAttributes("cardNumber")}
                    />
                    {errors.cardNumber && (
                      <p className="checkout-field__error" id="cardNumber-error">
                        {errors.cardNumber}
                      </p>
                    )}
                  </div>

                  <div className="checkout-field-row">
                    <div className="checkout-field">
                      <label htmlFor="expiry">Expiry date</label>
                      <input
                        id="expiry"
                        name="expiry"
                        type="text"
                        inputMode="numeric"
                        autoComplete="cc-exp"
                        value={formValues.expiry}
                        onChange={handleExpiryChange}
                        placeholder="MM / YY"
                        {...getErrorAttributes("expiry")}
                      />
                      {errors.expiry && (
                        <p className="checkout-field__error" id="expiry-error">
                          {errors.expiry}
                        </p>
                      )}
                    </div>
                    <div className="checkout-field">
                      <label htmlFor="securityCode">Security code</label>
                      <input
                        id="securityCode"
                        name="securityCode"
                        type="text"
                        inputMode="numeric"
                        autoComplete="cc-csc"
                        value={formValues.securityCode}
                        onChange={handleSecurityCodeChange}
                        placeholder="CVV"
                        {...getErrorAttributes("securityCode")}
                      />
                      {errors.securityCode && (
                        <p
                          className="checkout-field__error"
                          id="securityCode-error"
                        >
                          {errors.securityCode}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="checkout-field">
                    <label htmlFor="cardName">Name on card</label>
                    <input
                      id="cardName"
                      name="cardName"
                      type="text"
                      autoComplete="cc-name"
                      value={formValues.cardName}
                      onChange={handleInputChange}
                      {...getErrorAttributes("cardName")}
                    />
                    {errors.cardName && (
                      <p className="checkout-field__error" id="cardName-error">
                        {errors.cardName}
                      </p>
                    )}
                  </div>

                  <label className="checkout-checkbox">
                    <input
                      type="checkbox"
                      name="billingSame"
                      checked={formValues.billingSame}
                      onChange={handleBillingSameChange}
                    />
                    <span>Use shipping address as billing address</span>
                  </label>

                  {!formValues.billingSame && (
                    <div className="checkout-billing-fields">
                      <h3>Billing address</h3>
                      <div className="checkout-field">
                        <label htmlFor="billingCountry">Country / region</label>
                        <select
                          id="billingCountry"
                          name="billingCountry"
                          autoComplete="billing country"
                          value={formValues.billingCountry}
                          onChange={handleInputChange}
                          {...getErrorAttributes("billingCountry")}
                        >
                          <option value="">Select country / region</option>
                          {countryOptions.map(([code, name]) => (
                            <option key={code} value={code}>
                              {name}
                            </option>
                          ))}
                        </select>
                        {errors.billingCountry && (
                          <p
                            className="checkout-field__error"
                            id="billingCountry-error"
                          >
                            {errors.billingCountry}
                          </p>
                        )}
                      </div>
                      <div className="checkout-field">
                        <label htmlFor="billingAddress">Address</label>
                        <input
                          id="billingAddress"
                          name="billingAddress"
                          type="text"
                          autoComplete="billing address-line1"
                          value={formValues.billingAddress}
                          onChange={handleInputChange}
                          {...getErrorAttributes("billingAddress")}
                        />
                        {errors.billingAddress && (
                          <p
                            className="checkout-field__error"
                            id="billingAddress-error"
                          >
                            {errors.billingAddress}
                          </p>
                        )}
                      </div>
                      <div className="checkout-field-row">
                        <div className="checkout-field">
                          <label htmlFor="billingCity">City</label>
                          <input
                            id="billingCity"
                            name="billingCity"
                            type="text"
                            autoComplete="billing address-level2"
                            value={formValues.billingCity}
                            onChange={handleInputChange}
                            {...getErrorAttributes("billingCity")}
                          />
                          {errors.billingCity && (
                            <p
                              className="checkout-field__error"
                              id="billingCity-error"
                            >
                              {errors.billingCity}
                            </p>
                          )}
                        </div>
                        <div className="checkout-field">
                          <label htmlFor="billingPostalCode">Postal code</label>
                          <input
                            id="billingPostalCode"
                            name="billingPostalCode"
                            type="text"
                            autoComplete="billing postal-code"
                            value={formValues.billingPostalCode}
                            onChange={handleInputChange}
                            {...getErrorAttributes("billingPostalCode")}
                          />
                          {errors.billingPostalCode && (
                            <p
                              className="checkout-field__error"
                              id="billingPostalCode-error"
                            >
                              {errors.billingPostalCode}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <p className="checkout-demo-note">
                Demo checkout only — payment details are not processed or stored.
              </p>
            </fieldset>

            <button
              type="submit"
              className="checkout-submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Processing order…"
                : `Pay ${formatCurrency(orderTotal)}`}
            </button>

            <div className="checkout-legal-links">
              <Link to="/returns">Returns</Link>
              <Link to="/shipping">Shipping</Link>
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
            </div>
          </form>
        </section>

        <aside className="checkout-summary" aria-labelledby="checkout-summary-title">
          <div className="checkout-summary__inner">
            <h2 id="checkout-summary-title">Order summary</h2>

            <div className="checkout-summary__items">
              {checkoutLines.map((item) => {
                const productImage =
                  item.product.images?.[0] || item.product.image || "";
                const variantKey = `${item.id}-${item.size || "default"}-${
                  item.color || "default"
                }`;

                return (
                  <article className="checkout-product" key={variantKey}>
                    <div className="checkout-product__image-wrap">
                      <img
                        src={productImage}
                        alt={item.product.name}
                        loading="lazy"
                        decoding="async"
                      />
                      <span aria-label={`Quantity ${item.quantity}`}>
                        {item.quantity}
                      </span>
                    </div>
                    <div className="checkout-product__details">
                      <h3>{item.product.name}</h3>
                      {(item.color || item.size) && (
                        <p>
                          {[item.color, item.size].filter(Boolean).join(" / ")}
                        </p>
                      )}
                    </div>
                    <p className="checkout-product__price">
                      {formatCurrency(item.lineTotal)}
                    </p>
                  </article>
                );
              })}
            </div>

            <div className="checkout-summary__costs">
              <div>
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div>
                <span>Shipping</span>
                <span>{shipping === 0 ? "Free" : formatCurrency(shipping)}</span>
              </div>
            </div>

            <div className="checkout-summary__total">
              <span>
                Total <small>{siteConfig.currency || "EUR"}</small>
              </span>
              <strong>{formatCurrency(orderTotal)}</strong>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
